import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClockIcon } from 'lucide-react';
const STORAGE_KEY = 'swim_instructor_ready_countdown_target';
const ONE_DAY = 24 * 60 * 60 * 1000;
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + ONE_DAY;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!Number.isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + ONE_DAY;
  try {
    window.localStorage.setItem(STORAGE_KEY, target.toString());
  } catch {
    /* localStorage unavailable */
  }
  return target;
}
function pad(value: number) {
  return value.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(ONE_DAY);
  useEffect(() => setTarget(getInitialTarget()), []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const difference = target - Date.now();
      if (difference <= 0) {
        const nextTarget = Date.now() + ONE_DAY;
        try {
          window.localStorage.setItem(STORAGE_KEY, nextTarget.toString());
        } catch {
          /* localStorage unavailable */
        }
        setTarget(nextTarget);
        setRemaining(ONE_DAY);
        return;
      }
      setRemaining(difference);
    };
    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [target]);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="mt-10 max-w-3xl mx-auto bg-navy border border-gold/50 rounded-2xl p-6 md:p-8 shadow-[0_10px_30px_rgba(14,37,71,0.25)]" data-id="element-62" data-mp="1pz53wu618gjf">
      <div className="text-center" data-id="element-63" data-mp="2sadc3cn8x8if">
        <div className="flex items-center justify-center gap-2 text-gold-light font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-64" data-mp="2nd4ouxn8qeab">
          <AlarmClockIcon size={20} data-id="element-65" data-mp="2ja84p2i1ya49" />
          <span data-id="element-66" data-mp="2ed88rkex2nqv">The launch price ends soon</span>
        </div>
        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-67" data-mp="2ecorm5x3c3dw">
          <TimeBox value={pad(hours)} label="hrs" data-id="element-68" data-mp="29en7dhnxhy0u" />
          <span className="text-3xl md:text-5xl font-bold text-gold-light" data-id="element-69" data-mp="24n542myfnlcp">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-70" data-mp="24lbylazk376a" />
          <span className="text-3xl md:text-5xl font-bold text-gold-light" data-id="element-71" data-mp="1zp3le34x5o1r">:</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-72" data-mp="1vn9qyclbhga1" />
        </div>
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-white/80 mb-8" data-id="element-73" data-mp="1qndxv8e5r999">
          After this, the price goes up — and it won’t come back down.
        </p>
        <div className="space-y-2" data-id="element-74" data-mp="1lqdrsxhb66i5">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-75" data-mp="16ial5aaifdu">
            <span className="text-white/80" data-id="element-76" data-mp="64tkth4hyrwl">Launch pricing spots remaining</span>
            <span className="text-gold-light" data-id="element-77" data-mp="b4pg8rz4pb0j">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-navy-dark rounded-full overflow-hidden border border-gold/30" data-id="element-78" data-mp="b2lg8l4cow5j">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '65%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1,
            ease: [0.23, 1, 0.32, 1]
          }} className="h-full bg-gold rounded-full" data-id="element-79" data-mp="3el4xytpekjm3" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-white/70 font-medium" data-id="element-80" data-mp="3scjgz0jb8kt5">
            65% of spots taken
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-ivory-light border-2 border-gold rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px]" data-id="element-81" data-mp="2zupat9dff1la">
      <div className="flex items-baseline justify-center gap-1" data-id="element-82" data-mp="2gpj0pgqc425v">
        <span className="text-4xl md:text-5xl font-bold text-navy tabular-nums" data-id="element-83" data-mp="2btvcp4nl9f1y">{value}</span>
        <span className="text-xs md:text-sm font-medium text-gold-dark" data-id="element-84" data-mp="2qi8dcexmfm2s">{label}</span>
      </div>
    </div>;
}